/**
 * Simple module to drive the CSS vars --x and --y for elements with class .cmyk-spotlight
 * Keeps updates on requestAnimationFrame for performance.
 */

// Find the rendered text and inject wrapper markup at runtime, then init spotlight behavior.
// Safe client-side DOM replacement to avoid Shopify sanitizer blocking attributes.

const TARGET_TEXT = 'Pantum Printers & Toner Cartridges for UK Businesses';
const SELECTOR_TEXT_ELEMENTS = 'p, h1, h2, h3, h4, h5, h6, .rte, .text';

function escapeHtml(str) {
  return str
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function wrapIfMatch(el) {
  if (!el || el.dataset?.cmykWrapped === 'true') return false;
  const text = el.textContent?.trim();
  if (!text) return false;

  // Exact-match; adjust to .includes(...) if you want partial matches / localized variants.
  if (text === TARGET_TEXT) {
    const safe = escapeHtml(text);
    el.innerHTML = `<span class="cmyk-spotlight" tabindex="0"><span class="cmyk-base">${safe}</span><span class="cmyk-fill" aria-hidden="true">${safe}</span></span>`;
    el.dataset.cmykWrapped = 'true';
    return true;
  }
  return false;
}

function initSpotlightOn(el) {
  // el is the .cmyk-spotlight element
  if (!el || el._cmykInit) return;
  el._cmykInit = true;

  let raf = null;
  let lastX = null;
  let lastY = null;

  function setPos(x, y) {
    el.style.setProperty('--x', `${Math.round(x)}px`);
    el.style.setProperty('--y', `${Math.round(y)}px`);
  }

  function onMove(e) {
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (x === lastX && y === lastY) return;
    lastX = x;
    lastY = y;

    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => setPos(x, y));
  }

  function onEnter(e) {
    el.classList.add('active');
    onMove(e);
  }

  function onLeave() {
    el.classList.remove('active');
  }

  el.addEventListener('mousemove', onMove, { passive: true });
  el.addEventListener('mouseenter', onEnter);
  el.addEventListener('mouseleave', onLeave);

  // Keyboard focus support
  el.addEventListener('focus', () => {
    const rect = el.getBoundingClientRect();
    setPos(rect.width / 2, rect.height / 2);
    el.classList.add('active');
  });
  el.addEventListener('blur', onLeave);

  // Touch support
  el.addEventListener('touchstart', (e) => {
    const touch = e.touches[0];
    if (!touch) return;
    const rect = el.getBoundingClientRect();
    setPos(touch.clientX - rect.left, touch.clientY - rect.top);
    el.classList.add('active');
  }, { passive: true });
  el.addEventListener('touchend', onLeave, { passive: true });
}

function hydrate() {
  try {
    const candidates = document.querySelectorAll(SELECTOR_TEXT_ELEMENTS);
    let wrapped = 0;
    candidates.forEach((el) => {
      if (wrapIfMatch(el)) wrapped++;
    });

    if (wrapped === 0) {
      // Retry once after sections hydrate
      setTimeout(() => {
        document.querySelectorAll(SELECTOR_TEXT_ELEMENTS).forEach((el) => {
          if (wrapIfMatch(el)) wrapped++;
        });
        document.querySelectorAll('.cmyk-spotlight').forEach(initSpotlightOn);
        if (wrapped === 0) {
          // optional debug
          // console.debug('cmyk-spotlight: no target wrapped (retry)');
        }
      }, 600);
    } else {
      document.querySelectorAll('.cmyk-spotlight').forEach(initSpotlightOn);
    }
  } catch (err) {
    // Avoid breaking the page if something unexpected happens
    // eslint-disable-next-line no-console
    console.error('cmyk-spotlight hydrate error:', err);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrate);
} else {
  hydrate();
}